/**
 * 労基ルールの編集ページ (/tenant/labor-rule/:id/edit)
 *
 * 現状はモックデータの編集 UI のみ。保存処理は未実装。
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/atoms/Button/Button";
import {
    findLaborRuleById,
    type LaborCheckDetail,
    type LaborRuleRow,
    type LaborRuleStatus,
    type ScheduledWorkHoursDetail,
} from "./workRulesMockData";

interface FormState {
    status: LaborRuleStatus;
    scheduledWorkHours: ScheduledWorkHoursDetail | null;
    laborCheck: LaborCheckDetail | null;
}

const STATUS_OPTIONS: ReadonlyArray<LaborRuleStatus> = ["オン", "オフ"];

const NUMBER_INPUT_CLASS =
    "form-input-field w-20 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

const FormField = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className="flex items-start gap-6">
        <label className="w-32 text-sm text-gray-700 pt-2 flex-shrink-0">{label}</label>
        <div className="flex-1">{children}</div>
    </div>
);

interface HourMinuteInputProps {
    hour: number;
    minute: number;
    onChange: (next: { hour: number; minute: number }) => void;
}

const HourMinuteInput = ({ hour, minute, onChange }: HourMinuteInputProps) => (
    <div className="flex items-center gap-2">
        <input
            type="number"
            min={0}
            value={hour}
            onChange={(e) => onChange({ hour: Number(e.target.value) || 0, minute })}
            className={NUMBER_INPUT_CLASS}
        />
        <span className="text-sm text-gray-700">時間</span>
        <input
            type="number"
            min={0}
            max={59}
            value={minute}
            onChange={(e) => onChange({ hour, minute: Number(e.target.value) || 0 })}
            className={NUMBER_INPUT_CLASS}
        />
        <span className="text-sm text-gray-700">分</span>
    </div>
);

const toFormState = (row: LaborRuleRow): FormState => ({
    status: row.status,
    scheduledWorkHours: row.scheduledWorkHours
        ? {
              perDay: { ...row.scheduledWorkHours.perDay },
              perWeek: { ...row.scheduledWorkHours.perWeek },
          }
        : null,
    laborCheck: row.laborCheck
        ? {
              perDay: { ...row.laborCheck.perDay },
              perWeek: { ...row.laborCheck.perWeek },
              perMonth: row.laborCheck.perMonth ? { ...row.laborCheck.perMonth } : undefined,
              consecutiveDays: row.laborCheck.consecutiveDays,
          }
        : null,
});

const WorkRulesEditPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const ruleId = id ? Number(id) : NaN;
    const row = Number.isFinite(ruleId) ? findLaborRuleById(ruleId) : undefined;

    const [form, setForm] = useState<FormState | null>(null);

    useEffect(() => {
        if (row) {
            setForm(toFormState(row));
        }
    }, [row]);

    const handleCancel = () => navigate("/tenant/labor-rule");

    const handleSave = () => {
        // TODO: DB 連携時に PUT 処理を実装する。現状は一覧へ戻るのみ。
        navigate("/tenant/labor-rule");
    };

    return (
        <div className="flex flex-1 flex-col overflow-hidden">
            <PageHeader title="労基ルールの編集" />

            <div className="flex-1 overflow-auto px-6 pb-6">
                <div className="bg-white rounded-lg p-6 mb-4">
                    {!row || !form ? (
                        <div className="text-sm text-gray-500">
                            {row ? "読み込み中..." : "対象の労基ルールが見つかりません"}
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <FormField label="区分">
                                <input
                                    type="text"
                                    value={row.category}
                                    readOnly
                                    className="form-input-field w-60 bg-gray-50"
                                />
                            </FormField>

                            <FormField label="種類">
                                <input
                                    type="text"
                                    value={row.kind}
                                    readOnly
                                    className="form-input-field w-60 bg-gray-50"
                                />
                            </FormField>

                            <FormField label="ステータス">
                                <select
                                    value={form.status}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            status: e.target.value as LaborRuleStatus,
                                        })
                                    }
                                    className="form-select-field w-40"
                                >
                                    {STATUS_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            {form.scheduledWorkHours && (
                                <>
                                    <FormField label="1日あたり">
                                        <HourMinuteInput
                                            hour={form.scheduledWorkHours.perDay.hour}
                                            minute={form.scheduledWorkHours.perDay.minute}
                                            onChange={(perDay) =>
                                                setForm({
                                                    ...form,
                                                    scheduledWorkHours: {
                                                        ...form.scheduledWorkHours!,
                                                        perDay,
                                                    },
                                                })
                                            }
                                        />
                                    </FormField>
                                    <FormField label="1週間あたり">
                                        <HourMinuteInput
                                            hour={form.scheduledWorkHours.perWeek.hour}
                                            minute={form.scheduledWorkHours.perWeek.minute}
                                            onChange={(perWeek) =>
                                                setForm({
                                                    ...form,
                                                    scheduledWorkHours: {
                                                        ...form.scheduledWorkHours!,
                                                        perWeek,
                                                    },
                                                })
                                            }
                                        />
                                    </FormField>
                                </>
                            )}

                            {form.laborCheck && (
                                <>
                                    <FormField label="1日あたり">
                                        <HourMinuteInput
                                            hour={form.laborCheck.perDay.hour}
                                            minute={form.laborCheck.perDay.minute}
                                            onChange={(perDay) =>
                                                setForm({
                                                    ...form,
                                                    laborCheck: {
                                                        ...form.laborCheck!,
                                                        perDay,
                                                    },
                                                })
                                            }
                                        />
                                    </FormField>
                                    <FormField label="1週間あたり">
                                        <HourMinuteInput
                                            hour={form.laborCheck.perWeek.hour}
                                            minute={form.laborCheck.perWeek.minute}
                                            onChange={(perWeek) =>
                                                setForm({
                                                    ...form,
                                                    laborCheck: {
                                                        ...form.laborCheck!,
                                                        perWeek,
                                                    },
                                                })
                                            }
                                        />
                                    </FormField>
                                    {form.laborCheck.perMonth && (
                                        <FormField label="1か月あたり">
                                            <HourMinuteInput
                                                hour={form.laborCheck.perMonth.hour}
                                                minute={form.laborCheck.perMonth.minute}
                                                onChange={(perMonth) =>
                                                    setForm({
                                                        ...form,
                                                        laborCheck: {
                                                            ...form.laborCheck!,
                                                            perMonth,
                                                        },
                                                    })
                                                }
                                            />
                                        </FormField>
                                    )}
                                    {form.laborCheck.consecutiveDays != null && (
                                        <FormField label="連勤日数">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    value={form.laborCheck.consecutiveDays}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            laborCheck: {
                                                                ...form.laborCheck!,
                                                                consecutiveDays:
                                                                    Number(e.target.value) || 0,
                                                            },
                                                        })
                                                    }
                                                    className={NUMBER_INPUT_CLASS}
                                                />
                                                <span className="text-sm text-gray-700">日</span>
                                            </div>
                                        </FormField>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        size="md"
                        className="px-8 bg-white"
                        onClick={handleCancel}
                    >
                        キャンセル
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        className="px-8"
                        onClick={handleSave}
                    >
                        保存
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default WorkRulesEditPage;
